import { Suspense, lazy } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useGradient, useGradientStatus } from "../api/queries";
import { Card } from "../components/ui";
import { GradientCurves } from "../viz/GradientCurves";
import { GradientBlocks } from "../viz/GradientBlocks";
import { GradientPhenotype } from "../viz/GradientPhenotype";

const LossLandscape3D = lazy(() =>
  import("../viz/LossLandscape3D").then((m) => ({ default: m.LossLandscape3D })));

function Window({ title, wide, tall, children }: {
  title: string; wide?: boolean; tall?: boolean; children: ReactNode;
}) {
  return (
    <section className={`win${wide ? " win-wide" : ""}`}>
      <header className="win-head">{title}</header>
      <div className={`viz-host${tall ? " viz-host-tall" : ""}`}>{children}</div>
    </section>
  );
}

function fmtElapsed(s: number): string {
  if (!Number.isFinite(s)) return "—";
  const m = Math.floor(s / 60), sec = Math.round(s % 60);
  if (m >= 60) return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}m`;
  return m > 0 ? `${m}m${String(sec).padStart(2, "0")}s` : `${sec}s`;
}

function fmtMetric(v: number): string {
  if (!Number.isFinite(v)) return "—";
  const a = Math.abs(v);
  if (a !== 0 && (a >= 10000 || a < 0.001)) return v.toExponential(2);
  return a >= 100 ? v.toFixed(1) : v.toFixed(4);
}

// The Gradient training console. Surfaces the /api/v1/gradient telemetry blob;
// every panel renders only if its data key is present. Reached from the "gradient
// live" banner on the evolution dashboard, or directly via /gradient.
export function Gradient() {
  const status = useGradientStatus().data;
  // Fetch telemetry whenever it's present (running or freshly done). Default to
  // enabled until status resolves so a direct visit still fetches.
  const present = status?.present ?? true;
  const data = useGradient(present).data;

  const meta = data?.meta;
  const done = meta?.status === "done";

  return (
    <div className="grad-page">
      <header className="grad-head">
        <Link to="/" className="grad-back">← evolution dashboard</Link>
        <h1>Gradient training</h1>
      </header>

      {!data || !meta ? (
        <Card>
          <p className="dim">
            {present ? "loading telemetry…" : "waiting for telemetry — start a gradient run"}
          </p>
        </Card>
      ) : (
        <>
          <div className="dash-status mono">
            <span className="grad-dot" style={{ color: done ? "var(--good)" : "var(--accent)" }}>●</span>
            <span className="grad-task">{meta.task}</span>
            <span><i className="dim">status</i> {meta.status}</span>
            <span><i className="dim">step</i> {meta.step}{meta.total ? ` / ${meta.total}` : ""}</span>
            <span><i className="dim">loss</i> {fmtMetric(meta.loss)}</span>
            <span><i className="dim">elapsed</i> {fmtElapsed(meta.elapsed_s)}</span>
            {Object.entries(meta.metrics ?? {}).map(([k, v]) => (
              <span key={k}><i className="dim">{k}</i> {fmtMetric(v)}</span>
            ))}
          </div>

          {meta.total > 0 && (
            <div className="dash-progress">
              <div className="meter">
                <div
                  className="meter-fill"
                  style={{
                    width: `${(Math.min(1, meta.step / meta.total) * 100).toFixed(1)}%`,
                    background: done ? "var(--good)" : "var(--accent)",
                  }}
                />
              </div>
              <div className="dash-progress-label mono dim">step {meta.step} / {meta.total}</div>
            </div>
          )}

          <div className="grad-windows">
            <Window title="Training curves" wide>
              <GradientCurves rows={data.curve ?? []} />
            </Window>

            {data.blocks && data.blocks.length > 0 && (
              <Window title="Gradient by block — where learning flows">
                <GradientBlocks blocks={data.blocks} />
              </Window>
            )}

            {data.phenotype != null && data.phenotype.length > 0 && (
              <Window title="What the retina sees" tall>
                <GradientPhenotype clips={data.phenotype} />
              </Window>
            )}

            {data.landscape && data.landscape.z?.length > 1 && (
              <Window title="Loss landscape" wide tall>
                <Suspense fallback={<div className="viz-empty">loading 3D…</div>}>
                  <LossLandscape3D landscape={data.landscape} />
                </Suspense>
              </Window>
            )}
          </div>
        </>
      )}
    </div>
  );
}
